import { Event } from '@sentry/react'
import { analyticsEventFactory } from 'core/modules/analytics/eventsFactory'
import { debounce, DebouncedFuncLeading } from 'lodash'
import Module from 'module'
import { createContext, useContext, useInsertionEffect, useMemo } from 'react'

let msgDebounceFunc: {
  [msg: string]: DebouncedFuncLeading<(msg: string) => void>
} = {}

const TEST_ENV_LOG = (msg: string) => {
  if (!msgDebounceFunc[msg])
    msgDebounceFunc[msg] = debounce((msg: string) => {
      console.error(msg)
      if (!process.env.AUTOMATION) alert(msg)
    }, 100)
  msgDebounceFunc[msg](msg)
}
function loadSentry() {
  return import('@sentry/react').then((mod) => {
    return {
      client: mod.init({
        dsn:
          process.env.AUTOMATION || process.env.STORYBOOK
            ? undefined
            : 'https://8887afdb847b9745c5105f89212fe46d@o4508404831551488.ingest.us.sentry.io/4508404837056512',
        integrations: [],
      }),
      setExtraData(k: string, v: string | undefined) {
        mod.setExtra(k, v)
      },
      setUser(id: string) {
        mod.setUser({ id })
      },
      unsetUser() {
        mod.setUser(null)
      },
    }
  })
}
export const LoadSentryClient = () =>
  process.env.AUTOMATION || process.env.STORYBOOK
    ? Promise.resolve({
        client: {
          sendEvent({ message, tags }: Event) {
            TEST_ENV_LOG(`TAG: ${String(tags!.class)} CONTENT: ${message}`)
          },
        } as any,
        setExtraData(k: string, v: string | undefined) {},
        setUser() {},
        unsetUser() {},
      } satisfies Awaited<ReturnType<typeof loadSentry>>)
    : loadSentry()

const _logError = (cls: 'env' | 'dev', msg: string) => {
  LoadSentryClient().then((SentryClient) => {
    SentryClient?.client?.sendEvent({
      message: msg,
      tags: {
        class: cls,
        tenant: process.env.REACT_APP_TENANT,
      },
    })
  })
}

export const logEnvError = (msg: string) => _logError('env', msg)
export const logDevError = (msg: string, module: Module) =>
  _logError('dev', `${msg} at ${module.id}`)
export const setCustomInfo = (key: string, value: string | undefined) =>
  LoadSentryClient().then((client) => {
    client.setExtraData(key, value)
  })
export const setUserForLogs = (uid?: string) =>
  LoadSentryClient().then((client) => {
    if (uid) client.setUser(uid)
    else client.unsetUser()
  })

export const ErrorReportingContext = createContext<
  {
    [k in 'logEnvError']: (errorMsg: string) => void
  } & { logDevError: (errorMsg: string, module: Module) => void }
>({ logDevError, logEnvError })

const MAP = {
} satisfies { [k in keyof NodeJS.CustomProcessEnv]?: () => any }

export function useEnvironmentVariable<K extends keyof typeof MAP>(
  envVariable: K
): ReturnType<(typeof MAP)[K]> {
  const { logEnvError } = useContext(ErrorReportingContext)
  const envVariableConfig = useMemo(MAP[envVariable], []) as any
  useInsertionEffect(() => {
    if (!envVariableConfig)
      logEnvError(`The variable ${envVariable} is used but not defined`)
  }, [envVariableConfig])

  return envVariableConfig
}

export function useAnalyticsEvents() {
  const analytics = useMemo(() => {
    return require('../core/modules/firebase').analytics
  }, [])
  return analyticsEventFactory(analytics)
}

export function useDevelopmentError() {
  const { logDevError } = useContext(ErrorReportingContext)

  return logDevError
}
